import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { filter, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';
import { CostType } from '../models/cost-types.model';
import { combineLatest, of, Subject } from 'rxjs';
import { Advance } from '../models/advance.model';
import { Employee } from '../models/employee.model';

@Injectable({
  providedIn: 'root'
})
export class EmployeeService {

  private url = environment.url;

  private changeActiveStatusAction = new Subject();
  private searchAction = new Subject<string>();

  employees$ = this.http.get<Employee[]>(this.url + 'company/employees').pipe(
    map(employees => employees.map(Employee.adapt)),
    map((employees: Employee[]) => this.sortByLastName(employees) as Employee[]),
    share()
  );

  sortedEmployees$ = combineLatest([
    this.employees$,
    this.changeActiveStatusAction
  ]).pipe(
    map(([employees, changeActiveStatusAction]) => this.sortByLastName(employees) as Employee[])
  );


  searchedEmployees$ = combineLatest([
    this.employees$,
    this.searchAction,
  ]).pipe(
    map(([employees, searchTerm]) => employees.filter(
      (employee: Employee) => employee.person.lastName.toLowerCase().includes(searchTerm.toLowerCase().trim())
    )),
    map((employees: Employee[]) => this.sortByLastName(employees) as Employee[]) ,
  );


  activeEmployees$ = this.http.get<Employee[]>(this.url + 'company/employees/active').pipe(
    map((v: []) => v.map(Employee.adapt)),
    map((employees: Employee[]) => this.sortByLastName(employees) as Employee[]) ,
    shareReplay()
  );

  constructor(private http: HttpClient) {
  }


  private sortByActive(employees: Employee[]) {
    return employees.sort((a, b) => a.user.isActive ? -1 : b.user.isActive ? 1 : 0);
  }

  private sortByLastName(employees: Employee[]) {
    return employees.slice().sort((a, b) => {
      const lastName = this.comparePl(a.person && a.person.lastName, b.person && b.person.lastName);
      return lastName !== 0 ? lastName : this.comparePl(a.person && a.person.firstName, b.person && b.person.firstName);
    });
  }

  private comparePl(a: string, b: string): number {
    return (a || '').localeCompare(b || '', 'pl', { sensitivity: 'base' });
  }

  changeActiveStatusEmitAction() {
    this.changeActiveStatusAction.next();
  }

  searchEmployee(searachTerm) {
    this.searchAction.next(searachTerm);
  }
  fetchEmployee(id: number) {
    return this.http.get(this.url + 'employee/' + id).pipe(
      map(v => Employee.adapt(v))
    );

  }

  createEmployee(employee: Employee) {
    return this.http.post(this.url + 'employee', employee);
  }

  updateEmployee(employee: Employee, id) {
    return this.http.post(this.url + 'employee/' + id + '/update', employee);
  }

  changeActiveStatus(isActive: boolean, id: number) {
    return this.http.put(this.url + 'user/' + id + '/active/' + isActive, {});
  }

  fetchEmployeesByCostType(id: number) {
    if (id !== -1) {
      return this.http.get(this.url + 'costType/' + id + '/employees/register').pipe(
        map((v: []) => v.map(Employee.adapt))
      );

    } else {
      return this.employees$;
    }

  }

  fetchCostTypesPossibleToRegister(id: number) {
    if (id !== -1) {
      return this.http.get(this.url + 'employee/' + id + '/costTypes/register').pipe(
        map((v: []) => v.map(CostType.adapt)),
      );
    } else {
      return this.http.get(this.url + 'company/costTypes').pipe(
        map((v: []) => v.map(CostType.adapt)),
      );
    }
  }

  bulkUpdateCostTypePermissions(costTypeId: number, permissionTypes: string[], action: 'grant' | 'revoke') {
    return this.http.post(this.url + 'employee/bulk/costTypes', { costTypeId, permissionTypes, action });
  }

  anonymize(id) {
    return this.http.post(this.url + 'user/' + id + '/anonymize', { anonymize: true });
  }

  resetTwoFactor(id) {
    return this.http.post(this.url + 'user/' + id + '/2fa/reset', {});
  }

  fetchAdvances(id: number) {
    if (id !== -1) {
      return this.http.get(this.url + 'employee/' + id + '/advances').pipe(
        map((v: []) => v.map(Advance.adapt)),
        // map(v => [Advance.adapt({name: 'Bez zaliczki', id: -1}), ...v]),
      );
    } else {
      return of([]);
    }
  }

}
